import {
  currencySign,
  daysText,
  fmtNum,
  fmtSize,
  LINK_CHANNEL,
  numInput,
  parseNum,
  roDate,
  roDateTime,
  PARTIAL_BELOW,
  shortfallText,
  specsText,
  termsWarning,
  todayIso,
  type AdditionKind,
  type Currency,
  type OfferAddition,
  type OfferAdvance,
  type ItemTerms,
  type AltDecision,
  type OfferExtraction,
  type OfferPackage,
  type Rfq,
  type Send,
  type SendFile,
  type Transport,
  t,
  baseCountry,
  usesLei,
  vatRateOf,
} from '@procumi/shared';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { confirmDiscard, Dialog } from './Dialog';
import { draftOf, PackageEditor, packageOf, type PackageDraft } from './PackageEditor';
import { ask } from '../lib/confirm';
import { DateInput } from './DateInput';
import { isBuild } from '../lib/terms';
import { rich } from '../lib/rich';

/**
 * Oferta unui furnizor: documentele primite, citirea automată, prețurile.
 *
 * Citirea **nu salvează nimic**. Completează formularul și spune ce a înțeles,
 * poziție cu poziție; omul confirmă cu „Salvează oferta”. Documentele, în
 * schimb, se păstrează din clipa urcării — ele sunt dovada, nu o propunere.
 */

/** Ce acceptă serverul; lista vie e în apps/api/src/storage.ts. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,.txt';

/** Pe ce cale a venit oferta, dedus din ce am citit. Text liber în bază. */
/** Comanda minimă și ambalajul unei poziții, cum se scriu în formular. */
interface TermsDraft {
  minQty: string;
  packSize: string;
}

const termsDraft = (t: ItemTerms): TermsDraft => ({
  minQty: t.minQty == null ? '' : numInput(t.minQty),
  packSize: t.packSize == null ? '' : numInput(t.packSize),
});

/** Condițiile scrise; null dacă nu e nimic, 'invalid' dacă e scris ceva ce nu e număr pozitiv. */
function termsOfDraft(d: TermsDraft | undefined): ItemTerms | null | 'invalid' {
  if (!d) return null;
  const read = (v: string) => (v.trim() ? parseNum(v) : null);
  const minQty = read(d.minQty);
  const packSize = read(d.packSize);
  if (
    (d.minQty.trim() && (minQty == null || minQty <= 0)) ||
    (d.packSize.trim() && (packSize == null || packSize <= 0))
  ) {
    return 'invalid';
  }
  return minQty == null && packSize == null ? null : { minQty, packSize };
}

/** Un cost peste prețuri, cum se scrie în formular. */
interface ExtraDraft {
  label: string;
  amount: string;
}

/** O poziție pusă de furnizor în plus, cum se scrie în formular. */
interface AdditionDraft {
  desc: string;
  qty: string;
  unit: string;
  unitPrice: string;
  amount: string;
  kind: AdditionKind;
}

const additionDraft = (a: OfferAddition): AdditionDraft => ({
  desc: a.desc,
  qty: a.qty == null ? '' : numInput(a.qty),
  unit: a.unit,
  unitPrice: a.unitPrice == null ? '' : numInput(a.unitPrice),
  amount: a.amount == null ? '' : numInput(a.amount),
  kind: a.kind,
});

const ADDITION_KINDS: [AdditionKind, string][] = [
  ['accessory', t('Accesoriu')],
  ['consumable', t('Consumabil')],
  ['service', t('Serviciu')],
  ['other', t('Altele')],
];

/** '' = oferta nu spune nimic despre avans. */
type AdvanceKind = '' | OfferAdvance['kind'];

/** Cota de TVA scăzută când oferta e cu TVA. */
/** 1 + cota standard de TVA a țării firmei (România: 1,21). */
const vatRate = () => vatRateOf(baseCountry());
const VAT = () => 1 + vatRate() / 100;

/** Suma din casetă împărțită la TVA, înapoi în casetă; goală rămâne goală. */
function withoutVat(v: string): string {
  const n = v.trim() ? parseNum(v) : null;
  return n == null ? v : numInput(Math.round((n / VAT()) * 10_000) / 10_000);
}

function channelFor(mime: string): string {
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('image/')) return t('poză');
  return 'text';
}

export function OfferDialog({
  rfq,
  send,
  canExtract,
  inboxId,
  toast,
  onUpdated,
  onClose,
}: {
  rfq: Rfq;
  send: Send;
  /** citirea automată e pornită pe server (vezi /auth/me) */
  canExtract: boolean;
  /** oferta venită pe mail în căsuța agentului: formularul pornește cu ce a citit el */
  inboxId?: string;
  toast: Toast;
  onUpdated: (updated: Rfq) => void;
  onClose: () => void;
}) {
  const offer = send.offer;
  const [currency, setCurrency] = useState<Currency>(offer?.currency ?? 'RON');
  const [fxRate, setFxRate] = useState(offer?.fxRate ? numInput(offer.fxRate) : '');
  const [fxDate, setFxDate] = useState(offer?.fxDate ?? '');
  const [fxBusy, setFxBusy] = useState(false);
  // Prețurile se arată în moneda ofertei: pentru EUR, cele din document.
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      rfq.items.map((it) => {
        const v = offer?.packages?.[it.id]
          ? undefined
          : offer?.currency === 'EUR'
            ? offer.quoted?.[it.id]
            : offer?.prices[it.id];
        return [it.id, v == null ? '' : numInput(v)];
      }),
    ),
  );
  // Pozițiile ofertate pe set. Una cerută la chirie e mereu pe set: altfel nu
  // se știe pe ce perioadă e prețul.
  const [pkgs, setPkgs] = useState<Record<string, PackageDraft | undefined>>(() =>
    Object.fromEntries(
      rfq.items.map((it) => {
        const pkg = offer?.packages?.[it.id];
        return [it.id, pkg || it.rentDays != null ? draftOf(pkg, it) : undefined];
      }),
    ),
  );
  // Comanda minimă și ambalajul; undefined = câmpurile nu sunt deschise.
  const [terms, setTerms] = useState<Record<string, TermsDraft | undefined>>(() =>
    Object.fromEntries(
      rfq.items.map((it) => {
        const t = offer?.terms?.[it.id];
        return [it.id, t ? termsDraft(t) : undefined];
      }),
    ),
  );
  const [transport, setTransport] = useState<Transport>(offer?.transport ?? '');
  const [transportCost, setTransportCost] = useState(
    offer?.transportCost == null ? '' : numInput(offer.transportCost),
  );
  const [extras, setExtras] = useState<ExtraDraft[]>(
    () => offer?.extras?.map((e) => ({ label: e.label, amount: numInput(e.amount) })) ?? [],
  );
  // Cantitatea ofertată și produsul propus în loc; undefined = câmpul nu e deschis.
  const [offered, setOffered] = useState<Record<string, string | undefined>>(() =>
    Object.fromEntries(
      rfq.items.map((it) => {
        const q = offer?.offered?.[it.id];
        return [it.id, q == null ? undefined : numInput(q)];
      }),
    ),
  );
  const [alts, setAlts] = useState<Record<string, string | undefined>>(() =>
    Object.fromEntries(rfq.items.map((it) => [it.id, offer?.alts?.[it.id] || undefined])),
  );
  // Ticket 10: echivalentul se acceptă sau se respinge; nedecis nu intră la „cel mai mic”
  const [altDecisions, setAltDecisions] = useState<Record<string, AltDecision | undefined>>(() => ({
    ...(offer?.altDecisions ?? {}),
  }));
  const [advKind, setAdvKind] = useState<AdvanceKind>(offer?.advance?.kind ?? '');
  const [advValue, setAdvValue] = useState(
    offer?.advance?.value == null ? '' : numInput(offer.advance.value),
  );
  const [advText, setAdvText] = useState(offer?.advance?.text ?? '');
  const [paymentDays, setPaymentDays] = useState(offer?.paymentDays == null ? '' : String(offer.paymentDays));
  const [deliveryDays, setDeliveryDays] = useState(
    offer?.deliveryDays == null ? '' : String(offer.deliveryDays),
  );
  // „5 zile lucrătoare” rămân lucrătoare: fără weekend și sărbători (Ticket 08, P13)
  const [deliveryWorking, setDeliveryWorking] = useState(!!offer?.deliveryWorking);
  const [validUntil, setValidUntil] = useState(offer?.validUntil ?? '');
  const [offerDate, setOfferDate] = useState(offer?.offerDate ?? '');
  const [additions, setAdditions] = useState<AdditionDraft[]>(
    () => offer?.additions?.map(additionDraft) ?? [],
  );
  /** citirea n-a știut dacă prețurile au TVA: omul trebuie să spună, înainte de salvare */
  const [vatAsk, setVatAsk] = useState(false);
  const [delivery, setDelivery] = useState(offer?.delivery ?? '');
  const [payment, setPayment] = useState(offer?.payment ?? '');
  const [validity, setValidity] = useState(offer?.validity ?? '');
  const [notes, setNotes] = useState(offer?.notes ?? '');
  const [channel, setChannel] = useState(offer?.channel ?? '');
  /** textul ofertei: sursa citirii și, salvat, dovada de lângă prețuri */
  const [pasted, setPasted] = useState(offer?.rawText ?? '');
  const [read, setRead] = useState<OfferExtraction | null>(null);
  /** '' | id-ul documentului citit acum | 'text' */
  const [reading, setReading] = useState('');
  const [uploading, setUploading] = useState(false);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  // Ce s-a scris în formular, comparat cu ce era la deschidere. Documentele nu
  // intră aici: ele se salvează din clipa urcării.
  const form = JSON.stringify([
    prices,
    pkgs,
    currency,
    fxRate,
    terms,
    transport,
    transportCost,
    extras,
    delivery,
    payment,
    validity,
    notes,
    channel,
    pasted,
    offered,
    alts,
    advKind,
    advValue,
    advText,
    paymentDays,
    deliveryDays,
    deliveryWorking,
    validUntil,
    offerDate,
    additions,
  ]);
  const [initial] = useState(form);
  const dirty = form !== initial;
  const cancel = () => {
    void confirmDiscard(dirty).then((ok) => ok && onClose());
  };

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadSendFile(rfq.id, send.id, file);
      // Cererea se actualizează în pagină, dar dialogul rămâne deschis.
      onUpdated(result.rfq);
      toast(t('„{0}” a fost urcat', result.file.filename));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (f: SendFile) => {
    const ok = await ask({
      title: t('Ștergi documentul?'),
      message: t('„{0}” se șterge definitiv și nu se mai poate recupera.', f.filename),
      confirm: t('Șterge documentul'),
      danger: true,
    });
    if (!ok) return;
    try {
      onUpdated(await api.deleteSendFile(rfq.id, send.id, f.id));
      toast(t('Document șters'));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  /** Cursul BNR pentru ziua ofertei (sau ultimul publicat), pus în casetă. */
  const loadFx = async (date?: string) => {
    setFxBusy(true);
    try {
      const fx = await api.fxEur(date);
      setFxRate(numInput(fx.rate));
      setFxDate(fx.date);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setFxBusy(false);
    }
  };

  const changeCurrency = (c: Currency) => {
    setCurrency(c);
    if (c === 'EUR' && !fxRate) void loadFx(todayIso());
  };

  /** Propunerea citirii intră în formular. Prețurile găsite se suprascriu. */
  const apply = (r: OfferExtraction, how: string) => {
    setPrices((p) => {
      const next = { ...p };
      for (const [itemId, v] of Object.entries(r.prices)) next[itemId] = numInput(v);
      return next;
    });
    setPkgs((p) => {
      const next = { ...p };
      for (const it of rfq.items) {
        const pkg = r.packages[it.id];
        if (pkg) next[it.id] = draftOf(pkg, it);
      }
      return next;
    });
    setTerms((t) => {
      const next = { ...t };
      for (const [itemId, v] of Object.entries(r.terms)) next[itemId] = termsDraft(v);
      return next;
    });
    if (r.currency !== currency) setCurrency(r.currency);
    // cursul din ziua ofertei, nu din ziua de azi
    if (r.currency === 'EUR') void loadFx(r.offerDate || todayIso());
    // Ce n-a găsit citirea nu șterge ce era scris de mână.
    if (r.transport) {
      setTransport(r.transport);
      setTransportCost(r.transportCost == null ? '' : numInput(r.transportCost));
    }
    if (r.extras.length) {
      setExtras(r.extras.map((e) => ({ label: e.label, amount: numInput(e.amount) })));
    }
    if (r.delivery) setDelivery(r.delivery);
    if (r.payment) setPayment(r.payment);
    if (r.validity) setValidity(r.validity);
    if (r.notes) setNotes(r.notes);
    setOffered((o) => {
      const next = { ...o };
      for (const [itemId, v] of Object.entries(r.offered)) next[itemId] = numInput(v);
      return next;
    });
    setAlts((x) => ({ ...x, ...r.alts }));
    if (r.advance) {
      setAdvKind(r.advance.kind);
      setAdvValue(r.advance.value == null ? '' : numInput(r.advance.value));
      setAdvText(r.advance.text);
    }
    if (r.paymentDays != null) setPaymentDays(String(r.paymentDays));
    if (r.deliveryDays != null) {
      setDeliveryDays(String(r.deliveryDays));
      setDeliveryWorking(r.deliveryWorking);
    }
    if (r.validUntil) setValidUntil(r.validUntil);
    if (r.offerDate) setOfferDate(r.offerDate);
    if (r.additions.length) setAdditions(r.additions.map(additionDraft));
    setVatAsk(r.pricesIncludeVat);
    setChannel(how);
    setRead(r);
    const got = Object.keys(r.prices).length + Object.keys(r.packages).length;
    toast(t('Citit: {0} din {1} poziții. Verifică-le.', got, rfq.items.length));
  };

  // Agentul a citit deja oferta din mail: prețurile intră în formular o singură dată,
  // la deschidere; omul le verifică și salvează (nimic nu e salvat până atunci).
  const presetDone = useRef(false);
  useEffect(() => {
    if (!inboxId || presetDone.current) return;
    presetDone.current = true;
    void api
      .inboxExtraction(inboxId)
      .then((r) => {
        if (r.text && !pasted) setPasted(r.text.slice(0, 20_000));
        // încărcată de furnizor pe link: rămâne oferta lui de pe link, s-o poată schimba acolo
        if (r.extraction) apply(r.extraction, r.via === 'link' ? LINK_CHANNEL : 'email');
      })
      .catch((e) => toast(errMsg(e)));
  }, [inboxId]);

  const readFile = async (f: SendFile) => {
    setReading(f.id);
    try {
      apply(await api.extractOffer(rfq.id, send.id, { fileId: f.id }), channelFor(f.mime));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setReading('');
    }
  };

  const readText = async () => {
    const text = pasted.trim();
    if (!text) {
      toast(t('Lipește întâi textul ofertei'));
      return;
    }
    setReading('text');
    try {
      apply(await api.extractOffer(rfq.id, send.id, { text }), 'text');
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setReading('');
    }
  };

  const fx = currency === 'RON' ? 1 : parseNum(fxRate);

  /** Oferta era cu TVA: toate sumele din formular se împart la 1,21. */
  const removeVat = () => {
    setPrices((p) => Object.fromEntries(Object.entries(p).map(([k, v]) => [k, withoutVat(v)])));
    setPkgs((p) =>
      Object.fromEntries(
        Object.entries(p).map(([k, d]) => [
          k,
          d
            ? {
                ...d,
                total: withoutVat(d.total),
                oneTime: withoutVat(d.oneTime),
                replacement: withoutVat(d.replacement),
                // și liniile: altfel suma lor n-ar mai da totalul și tabelul ar avertiza degeaba
                lines: d.lines.map((l) => ({
                  ...l,
                  amount: Math.round((l.amount / VAT()) * 100) / 100,
                })),
              }
            : d,
        ]),
      ),
    );
    setTransportCost((v) => withoutVat(v));
    setExtras((x) => x.map((e) => ({ ...e, amount: withoutVat(e.amount) })));
    setAdditions((x) =>
      x.map((a) => ({ ...a, unitPrice: withoutVat(a.unitPrice), amount: withoutVat(a.amount) })),
    );
    setVatAsk(false);
    toast(t('TVA-ul de {0}% a fost scăzut din toate sumele', fmtNum(vatRate())));
  };

  /** Zile întregi din casetă; '' = null, altceva = 'invalid'. */
  const daysOf = (v: string): number | null | 'invalid' => {
    if (!v.trim()) return null;
    const n = parseNum(v);
    return n == null || n < 0 || !Number.isInteger(n) ? 'invalid' : n;
  };

  const save = async () => {
    const parsed: Record<string, number> = {};
    const packages: Record<string, OfferPackage> = {};
    const itemTerms: Record<string, ItemTerms> = {};
    if (vatAsk) {
      toast(t('Spune întâi dacă prețurile din ofertă sunt cu sau fără TVA'));
      return;
    }
    const itemOffered: Record<string, number> = {};
    const itemAlts: Record<string, string> = {};
    const itemAltDecisions: Record<string, AltDecision> = {};
    for (const it of rfq.items) {
      const alt = alts[it.id]?.trim();
      if (alt) itemAlts[it.id] = alt;
      const decided = altDecisions[it.id];
      if (alt && decided) itemAltDecisions[it.id] = decided;
      const d = pkgs[it.id];
      if (d) {
        if (!d.total.trim()) continue; // poziție necotată
        const pkg = packageOf(d);
        if (typeof pkg === 'string') {
          toast(`${it.desc}: ${pkg}`);
          return;
        }
        packages[it.id] = pkg;
        continue;
      }
      const n = parseNum(prices[it.id] ?? '');
      if (n == null) continue;
      parsed[it.id] = n;
      const td = termsOfDraft(terms[it.id]);
      if (td === 'invalid') {
        toast(t('{0}: comanda minimă și ambalajul trebuie să fie numere pozitive', it.desc));
        return;
      }
      if (td) itemTerms[it.id] = td;
      const q = offered[it.id]?.trim() ? parseNum(offered[it.id]!) : null;
      if (offered[it.id]?.trim() && (q == null || q <= 0)) {
        toast(t('{0}: cantitatea ofertată trebuie să fie un număr pozitiv', it.desc));
        return;
      }
      if (q != null && q !== it.qty) itemOffered[it.id] = q;
    }
    if (!Object.keys(parsed).length && !Object.keys(packages).length) {
      toast(t('Introdu cel puțin un preț'));
      return;
    }
    if (currency !== 'RON' && (fx == null || fx < 1 || fx > 20)) {
      toast(t('Scrie cursul: câți lei face un euro'));
      return;
    }
    // gol = suma nu e în ofertă; Procumi avertizează în tabel
    const cost = transport === 'extra' && transportCost.trim() ? parseNum(transportCost) : null;
    if (transport === 'extra' && transportCost.trim() && (cost == null || cost < 0)) {
      toast(t('Costul transportului nu e un număr'));
      return;
    }
    const costs: { label: string; amount: number }[] = [];
    for (const e of extras) {
      if (!e.label.trim() && !e.amount.trim()) continue; // rând lăsat gol
      const amount = parseNum(e.amount);
      if (!e.label.trim() || amount == null || amount <= 0) {
        toast(t('Fiecare cost are nevoie de ce e și de o sumă'));
        return;
      }
      costs.push({ label: e.label.trim(), amount });
    }
    const adds: OfferAddition[] = [];
    for (const a of additions) {
      if (!a.desc.trim() && !a.amount.trim() && !a.unitPrice.trim()) continue; // rând gol
      const read = (v: string) => (v.trim() ? parseNum(v) : null);
      const [qty, unitPrice, amount] = [read(a.qty), read(a.unitPrice), read(a.amount)];
      const bad = [
        [a.qty, qty],
        [a.unitPrice, unitPrice],
        [a.amount, amount],
      ].some(([raw, n]) => (raw as string).trim() && (n == null || (n as number) < 0));
      if (!a.desc.trim() || bad) {
        toast(t('Fiecare poziție suplimentară are nevoie de denumire și de numere pozitive'));
        return;
      }
      adds.push({ desc: a.desc.trim(), qty, unit: a.unit.trim(), unitPrice, amount, kind: a.kind });
    }
    const advNum = advValue.trim() ? parseNum(advValue) : null;
    if ((advKind === 'percent' || advKind === 'fixed') && (advNum == null || advNum <= 0)) {
      toast(advKind === 'percent' ? t('Scrie procentul avansului') : t('Scrie suma avansului'));
      return;
    }
    if (advKind === 'percent' && advNum! > 100) {
      toast(t('Avansul nu poate trece de 100%'));
      return;
    }
    const payDays = daysOf(paymentDays);
    const dlvDays = daysOf(deliveryDays);
    if (payDays === 'invalid' || dlvDays === 'invalid') {
      toast(t('Termenele de plată și de livrare se scriu în zile întregi'));
      return;
    }
    setBusy(true);
    try {
      const updated = await api.saveOffer(rfq.id, send.id, {
        prices: parsed,
        packages,
        terms: itemTerms,
        currency,
        ...(currency !== 'RON' ? { fxRate: fx!, ...(fxDate ? { fxDate } : {}) } : {}),
        transport,
        transportCost: cost,
        extras: costs,
        offered: itemOffered,
        alts: itemAlts,
        altDecisions: itemAltDecisions,
        advance: advKind
          ? {
              kind: advKind,
              value: advKind === 'percent' || advKind === 'fixed' ? advNum : null,
              text: advText.trim(),
            }
          : null,
        paymentDays: payDays,
        deliveryDays: dlvDays,
        deliveryWorking: dlvDays != null && deliveryWorking,
        validUntil: validUntil || null,
        offerDate: offerDate || null,
        additions: adds,
        delivery: delivery.trim(),
        payment: payment.trim(),
        validity: validity.trim(),
        notes: notes.trim(),
        channel,
        rawText: pasted.trim(),
      });
      onUpdated(updated);
      onClose();
      toast(t('Oferta de la {0} a fost salvată', send.supplier.name));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const found = read ? Object.keys(read.prices).length + Object.keys(read.packages).length : 0;
  const sign = currencySign(currency);

  return (
    <Dialog onClose={onClose} dirty={dirty}>
      <div className="dlg-h">
        <div>
          <h2>{t('Oferta de la {0}', send.supplier.name)}</h2>
          <p className="hint">{t('Prețuri fără TVA, în moneda ofertei. În tabel se compară în lei.')}</p>
        </div>
      </div>
      <div className="dlg-b">
        <div>
          <div className="section-title">
            <h3>{t('Documente primite')}</h3>
            <span className="muted" style={{ fontSize: 14 }}>
              {t('se păstrează imediat, chiar dacă nu salvezi prețurile')}
            </span>
          </div>
          {send.files.length ? (
            <div className="docs">
              {send.files.map((f) => (
                <div className="doc" key={f.id}>
                  <div className="nm">
                    {f.filename}
                    <span>{t('{0} · urcat {1}', fmtSize(f.size), roDateTime(f.createdAt))}</span>
                  </div>
                  <a href={api.sendFileUrl(rfq.id, send.id, f.id)}>{t('Descarcă')}</a>
                  {canExtract ? (
                    <button
                      className="btn small primary"
                      disabled={reading !== ''}
                      onClick={() => void readFile(f)}
                    >
                      {reading === f.id ? t('Citesc…') : t('Citește oferta')}
                    </button>
                  ) : null}
                  <button className="btn small" onClick={() => void removeFile(f)}>
                    {t('Șterge')}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div
            className={`drop${over ? ' over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              void upload(e.dataTransfer.files[0]);
            }}
          >
            <span>{t('Trage aici PDF-ul, Excel-ul sau poza primită de la furnizor')}</span>
            <label className="btn small">
              {uploading ? t('Se urcă…') : t('Alege fișier')}
              <input
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  void upload(file);
                }}
              />
            </label>
          </div>
        </div>

        <div>
          <label className="f">
            {t('Textul ofertei')}
            <textarea
              className="inp"
              rows={4}
              placeholder={t('Lipește aici mailul sau mesajul de pe WhatsApp…')}
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
            />
            <span className="hint">{t('Se salvează lângă prețuri, ca sursă a lor.')}</span>
          </label>
          {canExtract ? (
            <button
              className="btn"
              style={{ marginTop: 10 }}
              disabled={reading !== '' || !pasted.trim()}
              onClick={() => void readText()}
            >
              {reading === 'text' ? t('Citesc…') : t('Citește textul')}
            </button>
          ) : (
            <p className="hint">
              {t('Citirea automată nu e pornită pe acest server — prețurile se completează de mână.')}
            </p>
          )}
        </div>

        {read ? (
          <div className="read-note">
            {rich(
              'Citire automată: <b>{0}</b> din {1} poziții completate{2}. Verifică-le și salvează — până atunci nimic nu e în bază.',
              found,
              rfq.items.length,
              read.missing.length ? t(', {0} fără preț', read.missing.length) : '',
            )}
          </div>
        ) : null}
        {read?.checks.length ? (
          <div className="read-warn">
            {t('Verifică în ofertă:')}
            <ul className="checks-list">
              {read.checks.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {vatAsk ? (
          <div className="read-warn">
            {t(
              'Oferta nu spune clar dacă prețurile includ TVA. În tabel se compară prețuri fără TVA, deci spune cum sunt înainte de a salva.',
            )}
            <div className="vat-ask">
              <button className="btn small" onClick={() => setVatAsk(false)}>
                {t('Sunt fără TVA')}
              </button>
              <button className="btn small" onClick={removeVat}>
                {t('Sunt cu TVA: scade {0}%', fmtNum(vatRate()))}
              </button>
            </div>
          </div>
        ) : null}
        {read?.additions.length ? (
          <div className="read-note">
            {rich(
              'Oferta mai are <b>{0}</b> {1} în plus față de cerere. Le găsești mai jos, la „Poziții suplimentare”: se văd în tabel, dar nu intră în costul total.',
              read.additions.length,
              read.additions.length === 1 ? t('poziție') : t('poziții'),
            )}
          </div>
        ) : null}

        <div>
          {/* moneda și cursul BNR doar la firmele care lucrează în lei; altfel totul e în moneda firmei */}
          {usesLei() ? (
            <div className="fx-row">
              <label className="f">
                {t('Moneda ofertei')}
                <select
                  className="inp"
                  value={currency}
                  onChange={(e) => changeCurrency(e.target.value as Currency)}
                >
                  <option value="RON">{t('Lei')}</option>
                  <option value="EUR">{t('Euro')}</option>
                </select>
              </label>
              {currency !== 'RON' ? (
                <>
                  <label className="f">
                    {t('Curs (lei pentru 1 €)')}
                    <input
                      className="inp"
                      inputMode="decimal"
                      value={fxRate}
                      onChange={(e) => {
                        setFxRate(e.target.value);
                        setFxDate(''); // scris de mână, nu mai e cursul BNR al unei zile
                      }}
                    />
                  </label>
                  <div className="fx-src">
                    <span className="hint" style={{ margin: 0 }}>
                      {fxBusy
                        ? t('Iau cursul BNR…')
                        : fxDate
                          ? t('Cursul BNR din {0}', roDate(fxDate))
                          : t('Curs scris de mână')}
                    </span>
                    <button
                      className="btn small"
                      disabled={fxBusy}
                      onClick={() => void loadFx(fxDate || todayIso())}
                    >
                      {t('Ia cursul BNR')}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
          <div className="price-grid">
            {rfq.items.map((it) => {
              const note = read?.itemNotes[it.id];
              const missing = read?.missing.includes(it.id) ?? false;
              const draft = pkgs[it.id];
              const rent = it.rentDays != null;
              return (
                <Fragment key={it.id}>
                  <div className="d">
                    {it.desc}
                    <span>
                      {fmtNum(it.qty)} {it.unit}
                      {rent ? t(' · chirie, {0}', daysText(it.rentDays!)) : ''}
                      {specsText(it.specs) ? ` · ${specsText(it.specs)}` : ''}
                    </span>
                  </div>
                  {draft ? (
                    rent ? (
                      <span className="hint" style={{ margin: 0 }}>
                        {t('ofertă pe set')}
                      </span>
                    ) : (
                      <button
                        className="btn small"
                        onClick={() => setPkgs((p) => ({ ...p, [it.id]: undefined }))}
                      >
                        {t('Preț unitar')}
                      </button>
                    )
                  ) : (
                    <div className="price-in">
                      <input
                        className="inp"
                        inputMode="decimal"
                        aria-label={t('Preț unitar {0}', it.desc)}
                        placeholder={`${sign} / ${it.unit}`}
                        value={prices[it.id] ?? ''}
                        onChange={(e) => setPrices((p) => ({ ...p, [it.id]: e.target.value }))}
                      />
                      <span className="price-links">
                        {terms[it.id] ? null : (
                          <button
                            className="link"
                            onClick={() => setTerms((t) => ({ ...t, [it.id]: { minQty: '', packSize: '' } }))}
                          >
                            {t('minim / ambalaj')}
                          </button>
                        )}
                        <button
                          className="link"
                          onClick={() => setPkgs((p) => ({ ...p, [it.id]: draftOf(undefined, it) }))}
                        >
                          {t('e pe set')}
                        </button>
                        {offered[it.id] == null ? (
                          <button className="link" onClick={() => setOffered((o) => ({ ...o, [it.id]: '' }))}>
                            {t('altă cantitate')}
                          </button>
                        ) : null}
                        {alts[it.id] == null ? (
                          <button className="link" onClick={() => setAlts((x) => ({ ...x, [it.id]: '' }))}>
                            {t('alt produs')}
                          </button>
                        ) : null}
                      </span>
                    </div>
                  )}
                  {!draft && terms[it.id] ? (
                    <TermsEditor
                      item={it}
                      draft={terms[it.id]!}
                      onChange={(d) => setTerms((t) => ({ ...t, [it.id]: d }))}
                    />
                  ) : null}
                  {(!draft && offered[it.id] != null) || alts[it.id] != null ? (
                    <ItemOfferEditor
                      item={it}
                      offered={draft ? undefined : offered[it.id]}
                      alt={alts[it.id]}
                      altDecision={altDecisions[it.id]}
                      onOffered={(v) => setOffered((o) => ({ ...o, [it.id]: v }))}
                      onAlt={(v) => {
                        setAlts((x) => ({ ...x, [it.id]: v }));
                        // alt text = altă propunere: hotărârea de dinainte nu mai e valabilă
                        if (v?.trim() !== alts[it.id]?.trim())
                          setAltDecisions((x) => ({ ...x, [it.id]: undefined }));
                      }}
                      onAltDecision={(d) => setAltDecisions((x) => ({ ...x, [it.id]: d }))}
                    />
                  ) : null}
                  {draft ? (
                    <div className="pkg-row">
                      <PackageEditor
                        item={it}
                        draft={draft}
                        currency={currency}
                        fx={fx}
                        onChange={(d) => setPkgs((p) => ({ ...p, [it.id]: d }))}
                      />
                    </div>
                  ) : null}
                  {note || missing ? (
                    <div className={`pnote${missing ? ' warn' : ''}`}>
                      {missing ? t('Nu apare în ofertă. ') : ''}
                      {note}
                    </div>
                  ) : null}
                </Fragment>
              );
            })}
          </div>
          <div className="costs">
            <div className="row">
              <label className="f">
                {t('Transport')}
                <select
                  className="inp"
                  value={transport}
                  onChange={(e) => setTransport(e.target.value as Transport)}
                >
                  <option value="">{t('Oferta nu spune')}</option>
                  <option value="included">{t('Inclus în prețuri')}</option>
                  <option value="extra">{t('Se plătește separat')}</option>
                </select>
              </label>
              {transport === 'extra' ? (
                <label className="f">
                  {t('Cost transport ({0}, toată comanda)', sign)}
                  <input
                    className="inp"
                    inputMode="decimal"
                    placeholder={t('nu se știe')}
                    value={transportCost}
                    onChange={(e) => setTransportCost(e.target.value)}
                  />
                  <span className="hint">
                    {transportCost.trim()
                      ? t('Dacă e pe unitate, scrie suma pentru toată cantitatea.')
                      : t('Gol = suma nu e în ofertă. Tabelul te avertizează.')}
                  </span>
                </label>
              ) : null}
            </div>
            {extras.map((e, i) => (
              <div className="extra-row" key={i}>
                <label className="f">
                  {t('Alt cost')}
                  <input
                    className="inp"
                    placeholder={isBuild() ? t('descărcare cu macara') : t('livrare la rampă')}
                    maxLength={80}
                    value={e.label}
                    onChange={(ev) =>
                      setExtras((x) => x.map((y, j) => (j === i ? { ...y, label: ev.target.value } : y)))
                    }
                  />
                </label>
                <label className="f">
                  {t('Sumă ({0})', sign)}
                  <input
                    className="inp"
                    inputMode="decimal"
                    value={e.amount}
                    onChange={(ev) =>
                      setExtras((x) => x.map((y, j) => (j === i ? { ...y, amount: ev.target.value } : y)))
                    }
                  />
                </label>
                <button className="btn small" onClick={() => setExtras((x) => x.filter((_, j) => j !== i))}>
                  {t('Scoate')}
                </button>
              </div>
            ))}
            <button className="link" onClick={() => setExtras((x) => [...x, { label: '', amount: '' }])}>
              {t('+ Alt cost pe comandă (descărcare, paleți, taxă de comandă mică)')}
            </button>
          </div>
          <div className="costs">
            <div>
              <h3 style={{ margin: 0 }}>{t('Poziții suplimentare')}</h3>
              <span className="hint" style={{ margin: 0 }}>
                {t(
                  'Ce a pus furnizorul în plus față de cerere: console, popi, decofrol, montaj. Se văd în tabel, separat; nu intră în costul total.',
                )}
              </span>
            </div>
            {additions.map((a, i) => {
              const set = (patch: Partial<AdditionDraft>) =>
                setAdditions((x) => x.map((y, j) => (j === i ? { ...y, ...patch } : y)));
              return (
                <div className="add-edit" key={i}>
                  <label className="f">
                    {t('Denumire')}
                    <input
                      className="inp"
                      maxLength={300}
                      value={a.desc}
                      onChange={(e) => set({ desc: e.target.value })}
                    />
                  </label>
                  <label className="f">
                    {t('Cantitate')}
                    <input
                      className="inp"
                      inputMode="decimal"
                      value={a.qty}
                      onChange={(e) => set({ qty: e.target.value })}
                    />
                  </label>
                  <label className="f">
                    {t('UM')}
                    <input
                      className="inp"
                      maxLength={20}
                      value={a.unit}
                      onChange={(e) => set({ unit: e.target.value })}
                    />
                  </label>
                  <label className="f">
                    {t('Preț ({0})', sign)}
                    <input
                      className="inp"
                      inputMode="decimal"
                      value={a.unitPrice}
                      onChange={(e) => set({ unitPrice: e.target.value })}
                    />
                  </label>
                  <label className="f">
                    {t('Valoare ({0})', sign)}
                    <input
                      className="inp"
                      inputMode="decimal"
                      placeholder={t('cant. × preț')}
                      value={a.amount}
                      onChange={(e) => set({ amount: e.target.value })}
                    />
                  </label>
                  <label className="f">
                    {t('Fel')}
                    <select
                      className="inp"
                      value={a.kind}
                      onChange={(e) => set({ kind: e.target.value as AdditionKind })}
                    >
                      {ADDITION_KINDS.map(([k, label]) => (
                        <option key={k} value={k}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className="btn small"
                    onClick={() => setAdditions((x) => x.filter((_, j) => j !== i))}
                  >
                    {t('Scoate')}
                  </button>
                </div>
              );
            })}
            <button
              className="link"
              onClick={() =>
                setAdditions((x) => [
                  ...x,
                  { desc: '', qty: '', unit: t('buc'), unitPrice: '', amount: '', kind: 'accessory' },
                ])
              }
            >
              {t('+ Poziție suplimentară')}
            </button>
          </div>
          <div className="row" style={{ marginTop: 14 }}>
            <label className="f">
              {t('Avans')}
              <select
                className="inp"
                value={advKind}
                onChange={(e) => setAdvKind(e.target.value as AdvanceKind)}
              >
                <option value="">{t('Oferta nu spune')}</option>
                <option value="none">{t('Fără avans')}</option>
                <option value="percent">{t('Procent')}</option>
                <option value="fixed">{t('Sumă fixă')}</option>
                <option value="other">{t('Altă condiție')}</option>
              </select>
            </label>
            {advKind === 'percent' || advKind === 'fixed' ? (
              <label className="f">
                {advKind === 'percent' ? t('Avans (%)') : t('Avans ({0})', sign)}
                <input
                  className="inp"
                  inputMode="decimal"
                  value={advValue}
                  onChange={(e) => setAdvValue(e.target.value)}
                />
              </label>
            ) : null}
            {advKind && advKind !== 'none' ? (
              <label className="f">
                {t('Avans, cu cuvintele ofertei')}
                <input
                  className="inp"
                  maxLength={200}
                  placeholder={t('30% la semnarea contractului')}
                  value={advText}
                  onChange={(e) => setAdvText(e.target.value)}
                />
              </label>
            ) : null}
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <label className="f">
              {t('Termen plată (zile)')}
              <input
                className="inp"
                inputMode="numeric"
                placeholder="30"
                value={paymentDays}
                onChange={(e) => setPaymentDays(e.target.value)}
              />
              <span className="hint">{t('0 = la livrare')}</span>
            </label>
            <label className="f">
              {t('Condiții plată')}
              <input className="inp" value={payment} onChange={(e) => setPayment(e.target.value)} />
            </label>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <label className="f">
              {t('Termen livrare')}
              <span className="days-pair">
                <input
                  className="inp"
                  inputMode="numeric"
                  value={deliveryDays}
                  onChange={(e) => setDeliveryDays(e.target.value)}
                  aria-label={t('Termen livrare (zile)')}
                />
                <select
                  className="inp"
                  value={deliveryWorking ? 'w' : 'c'}
                  onChange={(e) => setDeliveryWorking(e.target.value === 'w')}
                  aria-label={t('Ce fel de zile')}
                >
                  <option value="c">{t('zile')}</option>
                  <option value="w">{t('zile lucrătoare')}</option>
                </select>
              </span>
            </label>
            <label className="f">
              {t('Termen livrare, cu cuvintele ofertei')}
              <input className="inp" value={delivery} onChange={(e) => setDelivery(e.target.value)} />
            </label>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <label className="f">
              {t('Data ofertei')}
              <DateInput value={offerDate} onChange={setOfferDate} />
            </label>
            <label className="f">
              {t('Valabilă până pe')}
              <DateInput value={validUntil} onChange={setValidUntil} />
            </label>
            <label className="f">
              {t('Valabilitate, cu cuvintele ofertei')}
              <input className="inp" value={validity} onChange={(e) => setValidity(e.target.value)} />
            </label>
          </div>
          <label className="f" style={{ marginTop: 12 }}>
            {t('Observații')}
            <input className="inp" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          {channel ? <p className="hint">{t('Sursa prețurilor: {0}', channel)}</p> : null}
        </div>
      </div>
      <div className="dlg-f">
        <button className="btn" onClick={cancel}>
          {t('Anulează')}
        </button>
        <button className="btn primary" disabled={busy} onClick={() => void save()}>
          {t('Salvează oferta')}
        </button>
      </div>
    </Dialog>
  );
}

/**
 * Ce ofertează furnizorul altfel decât s-a cerut, pe o poziție: mai puțină
 * cantitate („avem doar 500 mp”) sau alt produs („echivalent Baumit”).
 */
function ItemOfferEditor({
  item,
  offered,
  alt,
  altDecision,
  onOffered,
  onAlt,
  onAltDecision,
}: {
  item: { id: string; desc: string; qty: number; unit: string };
  /** undefined = câmpul nu e deschis */
  offered: string | undefined;
  alt: string | undefined;
  /** undefined = nedecis */
  altDecision: AltDecision | undefined;
  onOffered: (v: string | undefined) => void;
  onAlt: (v: string | undefined) => void;
  onAltDecision: (d: AltDecision | undefined) => void;
}) {
  const q = offered?.trim() ? parseNum(offered) : null;
  // aceeași regulă ca în tabel (`shortfall`), pe cantitatea scrisă acum
  const short =
    q != null && q > 0 && q < item.qty * PARTIAL_BELOW
      ? { requested: item.qty, offered: q, diff: q - item.qty }
      : null;
  return (
    <div className="terms-row">
      {offered != null ? (
        <label className="f">
          {t('Cantitate ofertată ({0})', item.unit)}
          <input
            className="inp"
            inputMode="decimal"
            placeholder={fmtNum(item.qty)}
            value={offered}
            onChange={(e) => onOffered(e.target.value)}
          />
        </label>
      ) : null}
      {alt != null ? (
        <label className="f">
          <span>
            {t('Produs propus în loc')} <span className="pill wait">{t('produs echivalent')}</span>
          </span>
          <input
            className="inp"
            maxLength={200}
            placeholder={t('echivalent Baumit MPI 25')}
            value={alt}
            onChange={(e) => onAlt(e.target.value)}
          />
        </label>
      ) : null}
      {alt?.trim() ? (
        <label className="f">
          {t('Îl accepți?')}
          <select
            className="inp"
            value={altDecision ?? ''}
            onChange={(e) => onAltDecision((e.target.value || undefined) as AltDecision | undefined)}
          >
            <option value="">{t('Încă nu')}</option>
            <option value="accepted">{t('Da')}</option>
            <option value="rejected">{t('Nu')}</option>
          </select>
        </label>
      ) : null}
      <button
        className="btn small"
        onClick={() => {
          onOffered(undefined);
          onAlt(undefined);
        }}
      >
        {t('Scoate')}
      </button>
      {short ? (
        <p className="pnote warn">
          {t(
            'Ofertă parțială: {0}. Se vede în tabel, dar nu câștigă comparația.',
            shortfallText(short, item.unit),
          )}
        </p>
      ) : null}
    </div>
  );
}

/** Comanda minimă și ambalajul unei poziții, sub preț, cu ce plătești de fapt. */
function TermsEditor({
  item,
  draft,
  onChange,
}: {
  item: { desc: string; qty: number; unit: string; id: string };
  draft: TermsDraft | undefined;
  onChange: (d: TermsDraft | undefined) => void;
}) {
  const d = draft ?? { minQty: '', packSize: '' };
  const td = termsOfDraft(d);
  const warn = td && td !== 'invalid' ? termsWarning(item, td) : null;
  return (
    <div className="terms-row">
      <label className="f">
        {t('Comandă minimă ({0})', item.unit)}
        <input
          className="inp"
          inputMode="decimal"
          value={d.minQty}
          onChange={(e) => onChange({ ...d, minQty: e.target.value })}
        />
      </label>
      <label className="f">
        {t('Se vinde doar câte ({0})', item.unit)}
        <input
          className="inp"
          inputMode="decimal"
          placeholder={t('palet, rolă')}
          value={d.packSize}
          onChange={(e) => onChange({ ...d, packSize: e.target.value })}
        />
      </label>
      <button className="btn small" onClick={() => onChange(undefined)}>
        {t('Scoate')}
      </button>
      {warn ? <p className="pnote warn">{warn[0]!.toUpperCase() + warn.slice(1)}.</p> : null}
      {td === 'invalid' ? <p className="pnote warn">{t('Scrie numere mai mari decât zero.')}</p> : null}
    </div>
  );
}
